import React, { useEffect, useState, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { allThreads, createSearchThread } from "../api/api";
import WorkflowCard from "../components/WorkflowCard"
import Profile from '../components/Profile';
import { useAuth } from "../utils/AuthContext";
import { useNavigate } from "react-router-dom";
import { Layers, Workflow, Activity , ChevronLeft, Plus, Search, Grid3X3, List, ChevronRight, ScanText, Star, Clock, MoreHorizontal, ArrowRight, CheckCircle, DollarSign, Target, Sparkles, Eye, Play, X} from "lucide-react";
import CustomLoader from '../components/CustomLoader';
import QUICK_TEMPLATES from '../utils/QuickTemplates'
import AIReportTemplate from '../components/AIReportTemplate';
import WorkflowLimitButton from '../components/WorkflowLimitButton'
import { ToastContainer, toast } from 'react-toastify';

// Helper function for random gradient backgrounds
const getRandomGradient = () => {
  const gradients = [
    "from-blue-400 to-blue-600",
    "from-indigo-400 to-purple-600",
    "from-cyan-400 to-blue-600",
    "from-blue-500 to-indigo-600",
    "from-purple-400 to-indigo-600",
    "from-teal-400 to-blue-600",
  ];
  return gradients[Math.floor(Math.random() * gradients.length)];
};

const TemplateCard = ({ template, onUse, onCopy, onPreview, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isPro = featured || template.isPro;

  return (
    <motion.div
      whileHover={{ y: -6, scale: 1.015 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      transition={{ type: "spring", stiffness: 230, damping: 18 }}
      className={`relative overflow-hidden rounded-2xl border backdrop-blur-md shadow-sm hover:shadow-xl transition-all group 
        ${isPro ? "border-transparent bg-gradient-to-br from-blue-500/10 via-indigo-500/5 to-purple-500/10" : "border-gray-100 bg-white/90"}`}
    >
      {/* Glowing Border for Pro */}
      {isPro && (
        <motion.div
          className="absolute inset-0 rounded-2xl border border-transparent bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 opacity-60 blur-[2px]"
          animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.02, 1],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      {/* Animated Overlay */}
      {isPro && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-indigo-50/20 to-purple-50/30"
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{ backgroundSize: "200% 200%" }}
        />
      )}

      {/* Floating Badge */}
      {isPro && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-3 right-3 z-20"
        >
          <div className="relative flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md">
            <Sparkles size={12} />
            <span>AI Recommended</span>

            {/* Shimmer Line */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent rounded-full"
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      )}

      {/* Card Content */}
      <div className="relative z-10 p-5">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <motion.div
            animate={isHovered ? { rotate: [0, 10, -10, 0] } : {}}
            transition={{ duration: 0.6 }}
            className="text-3xl"
          >
            {template.emoji}
          </motion.div>
          <div className="flex-1">
            <h3
              className={`text-lg font-semibold mb-1 ${
                isPro
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                  : "text-gray-900"
              } group-hover:opacity-90 transition-opacity`}
            >
              {template.title}
            </h3>
            <div className="relative">
              <p
                className="text-sm text-gray-600 leading-snug line-clamp-2 min-h-[40px]"
                title={template.desc} // shows full text on hover
              >
                {template.desc}
              </p>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {template.tags.map((tag) => (
            <motion.span
              key={tag}
              whileHover={{ scale: 1.05 }}
              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                isPro
                  ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                  : "bg-gray-50 text-gray-700 border-gray-100"
              }`}
            >
              {tag}
            </motion.span>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="flex flex-col items-center p-2 bg-blue-50/60 rounded-xl">
            <Clock size={16} className="text-blue-600 mb-1" />
            <span className="text-xs text-gray-700">{template.estTime}</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-green-50/60 rounded-xl">
            <DollarSign size={16} className="text-green-600 mb-1" />
            <span className="text-xs text-gray-700">{template.estCost}</span>
          </div>
          <div className="flex flex-col items-center p-2 bg-purple-50/60 rounded-xl">
            <Target size={16} className="text-purple-600 mb-1" />
            <span className="text-xs text-gray-700">{template.confidence}%</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onPreview(template)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-sm font-medium rounded-lg shadow-md hover:shadow-lg
              ${
                isPro
                  ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600"
                  : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600"
              }`}
          >
            <Eye size={14} />
            Preview
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onCopy(template.desc)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <ScanText size={14} />
            Copy Prompt
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const MiniLoader = () => (
  <motion.div
    className="flex flex-col items-center justify-center py-10 text-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4 }}
  >
    <div className="relative w-10 h-10 mb-4">
      <motion.div
        className="absolute inset-0 rounded-full border-4 border-blue-200"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.6, 0.3, 0.6],
        }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-blue-400 border-l-transparent border-b-transparent"
        animate={{ rotate: 360 }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
      />
    </div>
    <motion.p
      className="text-sm font-medium text-gray-600"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    >
      Loading your workflows...
    </motion.p>
  </motion.div>
);


export default function TulyFlow() {
  const { user } = useAuth();
  const token = Cookies.get("authToken");
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState([]);
  const [threads, setThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [currentThreadName, setCurrentThreadName] = useState("Default");
  const [pinned, setPinned] = useState({});
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [templatePreview, setTemplatePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isWorkflowEnable, setWorkflowEnable] = useState(true);
  
  const [workflowLimits, setWorkflowLimit] = useState(0);
  const [currentWorkflows, setCurrentWorkflows] = useState(0);

  const [searchLimit, setSearchLimit] = useState(0);

  const scrollRef = useRef(null);

  // Auto slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollBy({
          left: -300, // move left (rtl)
          behavior: "smooth",
        });
      }
    }, 4000); // every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? 300 : -300, // RTL movement
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    fetchThreads();
    // eslint-disable-next-line
  }, []);

  async function fetchThreads() {
    setLoading(true);
    try {
      const data = (await allThreads(token)) || [];
      const enriched = data.threads.map((t) => ({
        ...t,
        lastUsed: t.updatedAt || t.lastUsed || t.createdAt,
      }));
      
      // sort pinned first, then by lastUsed desc
      enriched.sort(
        (a, b) =>
          (pinned[b.threadId] ? 1 : 0) -
          (pinned[a.threadId] ? 1 : 0) ||
          new Date(b.lastUsed) - new Date(a.lastUsed)
      );

      setThreads(enriched);
      setWorkflows(enriched);

      if (!currentThreadId && enriched.length) {
        setCurrentThreadId(enriched[0].threadId);
        setCurrentThreadName(enriched[0].threadName);
        setSelectedWorkflow(enriched[0].threadId);
      }

      const searchesLimit = data.user_limit.maxSearches;
      setSearchLimit(searchesLimit);

      const workflowLimit = data.user_limit.maxWorkflows;
      setWorkflowLimit(workflowLimit);

      const currentWorkflowLength = data.threads.length;
      setCurrentWorkflows(currentWorkflowLength);

      if( currentWorkflowLength >= workflowLimit){
        setWorkflowEnable(false);
      }

      setLoading(false);

    } catch (e) {
      console.error("fetchThreads", e);
      setLoading(false);
    }
  }

  async function createThread(name = "") {
    const threadId = uuidv4();
    const threadName = name || `Workflow — ${new Date().toLocaleDateString()}`;
    try {
      await createSearchThread(token, threadId, threadName);
      await fetchThreads();
      setCurrentThreadId(threadId);
      setCurrentThreadName(threadName);
      setShowCreateModal(false);
      setNewWorkflowName("");
    } catch (e) {
      console.error("createThread", e);
    }
  }

  const handleCreateWorkflow = () => {
    if (!newWorkflowName.trim()) return;
    createThread(newWorkflowName);
  };

  const onCopy = (query) => { 
    navigator.clipboard?.writeText(query); 
    toast("Prompt copied to clipboard!");
  }

  const closeReport = () => {
    console.log('Arrived here');
    setTemplatePreview(null);
  }

  const handlePinWorkflow = (workflow) => {
    setWorkflows((prev) =>
      prev.map((w) =>
        w.threadId === workflow.threadId
          ? { ...w, pinned: !w.pinned }
          : w
      )
    );
    setPinned((prev) => ({
      ...prev,
      [workflow.threadId]: !prev[workflow.threadId],
    }));
  };

  function openThread(thread) {
    setCurrentThreadId(thread.threadId);
    setSelectedWorkflow(thread.threadId);
    setCurrentThreadName(thread.threadName);
    navigate("/workflow", {
      state: {
        threadId: thread.threadId,
        threadName: thread.threadName,
        searchlimit: searchLimit
      },
    });
  }

  // Filter logic
  const filteredWorkflows = workflows.filter((w) =>
    w.threadName?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredTemplates = QUICK_TEMPLATES.filter(
    (t) =>
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <ToastContainer
        position="top-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"/>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src="/tuly.png"
                alt="Tuly Logo" 
                className="h-12 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
          </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search workflows & templates"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70"
                />
              </div>
              <div className="flex items-center rounded-lg border border-gray-200">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${
                    viewMode === "grid"
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400"
                  }`}
                  title="Grid">
                  <Grid3X3 size={18} />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${
                    viewMode === "list"
                      ? "bg-blue-100 text-blue-600"
                      : "text-gray-400"
                  }`}
                  title="List">
                  <List size={18} />
                </button>
              </div>

              {/* Right Section: Search and Profile */}
              <div className="flex items-center space-x-2">

                {/* Profile Component with slight margin */}
                <div className="ml-2">
                  <Profile />
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Two Sections */}
      <main className="max-w-5xl mx-auto px-4 py-10 flex flex-col gap-10">

        {/* Account Usage Overview */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            {
              title: "Workflows",
              value: workflows.length,
              limit: workflowLimits,
              icon: Workflow,
              color: "blue",
              accent: "from-blue-500 to-indigo-400",
              progress: (workflows.length / workflowLimits) * 100,
            },
            {
              title: "Total Runs",
              value: workflows.reduce((sum, w) => sum + (w.searches?.length || 0), 0),
              limit: searchLimit * workflowLimits,
              icon: Activity,
              color: "emerald",
              accent: "from-emerald-500 to-teal-400",
              progress:
                (workflows.reduce((sum, w) => sum + (w.searches?.length || 0), 0) /
                  (searchLimit * workflowLimits)) *
                100,
            },
            {
              title: "Favorites",
              value: workflows.filter((w) => w.pinned).length,
              limit: null,
              icon: Star,
              color: "violet",
              accent: "from-violet-500 to-fuchsia-400",
              progress: null,
            },
          ].map((item, idx) => {
            const Icon = item.icon;
            const progressColor =
              item.progress > 85
                ? "from-red-500 to-rose-400"
                : item.accent;

            return (
              <div
                key={idx}
                className="relative overflow-hidden rounded-3xl bg-white p-6 border border-gray-200 shadow-[0_8px_25px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_35px_rgba(0,0,0,0.08)] transition-all duration-500 group"
              >
                {/* Color gradient glow background */}
                <div
                  className={`absolute inset-0 opacity-[0.12] group-hover:opacity-[0.18] transition-opacity duration-500 bg-gradient-to-br ${item.accent}`}
                ></div>

                {/* Floating color ring behind icon */}
                <div
                  className={`absolute top-6 left-6 w-16 h-16 rounded-full bg-gradient-to-br ${item.accent} blur-2xl opacity-30`}
                ></div>

                <div className="relative flex items-center gap-4">
                  {/* Icon bubble */}
                  <div
                    className={`relative z-10 p-3.5 rounded-2xl bg-gradient-to-br ${item.accent} text-white shadow-lg`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Title + Stats */}
                  <div>
                    <p className="text-sm font-medium text-gray-600 tracking-wide">
                      {item.title}
                    </p>
                    <p className="text-[1.6rem] font-extrabold text-gray-900 leading-tight">
                      {item.value}
                      {item.limit && (
                        <span className="text-gray-400 text-lg font-semibold">
                          {" "}
                          / {item.limit}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                {item.limit && (
                  <div className="relative mt-6">
                    <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className={`h-3 bg-gradient-to-r ${progressColor} transition-all duration-700 ease-in-out`}
                        style={{ width: `${item.progress}%` }}
                      ></div>
                    </div>
                    <p className="mt-2 text-xs font-medium text-gray-500">
                      {Math.round(item.progress)}% used
                    </p>
                  </div>
                )}

                {/* Favorites note */}
                {!item.limit && (
                  <p className="text-sm text-gray-500 mt-6 italic">
                    Your most loved workflows — easy to launch anytime 💜
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Workflows Section */}
        <section className="w-full">
          <div className="flex items-center justify-between mb-7">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                <Layers size={20} className="mr-2 text-blue-600" />
                Your Workflows
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Manage and organize your workflows
              </p>
            </div>
            {isWorkflowEnable &&
              <button onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm">
                <Plus size={16} />
                <span className="font-medium">New</span>
              </button>
            }
            
            {!isWorkflowEnable &&
              <WorkflowLimitButton
                isWorkflowEnabled={false}
                currentCount={currentWorkflows}
                maxLimit={workflowLimits}
              />
            }
          </div>

          <div className={`${loading ? "flex items-center justify-center " : `grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}`}>
            {loading ? (
              <MiniLoader key="loader" />
            ) : filteredWorkflows.length > 0 ? (   
              filteredWorkflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.threadId}
                  workflow={workflow}
                  maxSearchPerWorkflow={searchLimit}
                  isSelected={selectedWorkflow === workflow.threadId}
                  onSelect={openThread}
                  onPin={handlePinWorkflow}
                  onMenu={(w) => console.log("Menu for", w.threadName)}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <Layers size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No workflows found</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Create your first workflow
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Templates Section */}
        <section className="w-full relative">
          <div className="flex items-center justify-between mb-7">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                <Sparkles size={24} className="mr-3 text-blue-600" />
                Workflow Templates
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                Pre-built workflows to get you started quickly
              </p>
            </div>
            
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Left Button */}
            <button
              onClick={() => scroll("left")}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronLeft className="w-5 h-5 text-blue-600" />
            </button>

            {/* Right Button */}
            <button
              onClick={() => scroll("right")}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg p-2 rounded-full hover:bg-gray-100"
            >
              <ChevronRight className="w-5 h-5 text-blue-600" />
            </button>

            {/* Scrollable List */}
            <div
              ref={scrollRef}
              className="flex overflow-x-auto gap-6 scrollbar-hide scroll-smooth pr-4"
              dir="rtl"
            >
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="flex-shrink-0 w-[280px] sm:w-[320px]"
                >
                  <TemplateCard
                    template={template}
                    onUse={(t) => console.log("Using template:", t.title)}
                    onPreview={setTemplatePreview}
                    onCopy={onCopy}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
      {/* Create Workflow Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-1xl p-6 w-full max-w-md shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Create New Workflow
                </h3>
                <p className="text-gray-600 mt-1">
                  Give your workflow a descriptive name
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workflow Name
                  </label>
                  <input
                    type="text"
                    value={newWorkflowName}
                    onChange={(e) => setNewWorkflowName(e.target.value)}
                    placeholder="e.g., Social Media Campaign"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewWorkflowName("");
                    }}
                    className="flex-1 py-3 px-4 border border-gray-200 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateWorkflow}
                    disabled={!newWorkflowName.trim()}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 font-medium disabled:opacity-50"
                  >
                    Create
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Template Preview Modal */}
      <AnimatePresence>
        {templatePreview && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeReport} // Close when clicking overlay
          >
            {/* Close Button (outside modal, top-left) */}
            <button
              onClick={closeReport}
              className="absolute top-6 left-6 p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition"
              title="Close"
            >
              <X size={20} className="text-gray-600" />
            </button>

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl p-6 w-[70vw] shadow-2xl max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
              <AIReportTemplate
                reportData={templatePreview.report}
                closeReport={closeReport}
                onBackToSearch={() => setTemplatePreview(null)}
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>


      {/* Custom Styles */}
      <style jsx>{`
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
      `}</style>
    </div>
  );
}